import { QueryFunction, UseQueryOptions } from "@tanstack/react-query";
import { useRef, useState } from "react";

let _uid = 0;
const uniqueId = (prefix = "") => `${prefix}${++_uid}`;

export const useQueryScope = <TQueryFnData, TData = TQueryFnData>({
  queryFn,
  onSuccess,
  onError,
  onSettled,
}: {
  queryFn: QueryFunction<TQueryFnData>;
  onSuccess?: UseQueryOptions<TData, Error>["onSuccess"];
  onError?: UseQueryOptions<TData, Error>["onError"];
  onSettled?: UseQueryOptions<TData, Error>["onSettled"];
}): {
  queryFn: typeof queryFn;
  onSuccess: typeof onSuccess;
  onError: typeof onError;
  onSettled: typeof onSettled;
} => {
  const [instanceId] = useState(() => uniqueId());
  const requesterRef = useRef("");

  return {
    queryFn: (...args) => {
      requesterRef.current = instanceId;
      return queryFn(...args);
    },
    onSuccess: (...args) => {
      if (requesterRef.current === instanceId) {
        onSuccess?.(...args);
      }
    },
    onError: (...args) => {
      if (requesterRef.current === instanceId) {
        onError?.(...args);
      }
    },
    onSettled: (...args) => {
      if (requesterRef.current === instanceId) {
        onSettled?.(...args);
      }
    },
  };
};

/**
 * Calls every function with the same arguments and drops what they return.
 *
 * Right for *query* callbacks, whose return values react-query ignores anyway. Wrong for a
 * mutation's: `onMutate` returns the rollback snapshot react-query hands back as `context`, so
 * routing it through here severs that channel silently. Mutations use `mergeMutationCallbacks`.
 */
export const mergeFns = <TFunction extends (...args: never[]) => unknown>(
  ...fns: (TFunction | undefined)[]
): TFunction => {
  return ((...args) => {
    fns.forEach((fn) => fn?.(...args));
  }) as TFunction;
};
